import { DynamicConfigEntry } from '../../types/dynamicConfig';

/**
 * #1113 — the type-aware Runtime Config value editor, extracted from `RuntimeConfigTab` so the raw
 * key list and the Finance cards share one control per key type and one validation path (the key's
 * own min/max/allowedValues from the API).
 */
export default function ConfigKeyEditor({
  entry,
  value,
  disabled,
  onChange,
  id,
  ariaLabel,
}: {
  entry: DynamicConfigEntry;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  id?: string;
  ariaLabel?: string;
}) {
  const common = { id, disabled, 'aria-label': ariaLabel };

  if (entry.type === 'BOOLEAN') {
    return (
      <select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  if (entry.allowedValues.length > 0) {
    return (
      <select {...common} value={value} onChange={(e) => onChange(e.target.value)}>
        {entry.allowedValues.map((allowed) => (
          <option key={allowed} value={allowed}>{allowed}</option>
        ))}
      </select>
    );
  }

  if (entry.type === 'NUMBER') {
    return (
      <input
        {...common}
        type="number"
        value={value}
        min={entry.min ?? undefined}
        max={entry.max ?? undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  // STRING and DURATION. The placeholder is the only hint an admin gets about the ISO-8601 shape,
  // and the backend rejects anything else with a message that names what it wanted.
  return (
    <input
      {...common}
      type="text"
      value={value}
      placeholder={entry.type === 'DURATION' ? 'e.g. PT1H' : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * A sensible starting value for a key with no override, so the editor is never blank. It is NOT a
 * claim about what the service is running — the "Using the deployed value" note says that.
 */
export function defaultDraft(entry: DynamicConfigEntry): string {
  if (entry.type === 'BOOLEAN') return 'false';
  if (entry.allowedValues.length > 0) return entry.allowedValues[0];
  if (entry.type === 'NUMBER') return entry.min !== null ? String(entry.min) : '0';
  if (entry.type === 'DURATION') return 'PT1H';
  return '';
}

export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * Who last changed a key: the resolved name wins; a raw subject id is shortened to "Admin <last 8>"
 * (the ActorChip rule), never shown in full.
 */
export function actorLabel(updatedBy: string | null, updatedByName: string | null): string | null {
  if (updatedByName?.trim()) return updatedByName.trim();
  if (!updatedBy) return null;
  return /^[0-9a-f-]{36}$/i.test(updatedBy) ? `Admin ${updatedBy.slice(-8)}` : updatedBy;
}

/** A sensitive value as the Finance cards' history shows it: its length only. */
export function maskValue(value: string | null): string | null {
  if (value === null) return null;
  return `••• (${value.length} chars)`;
}

/**
 * Client-side check of a NUMBER value against the key's own bounds (from the API, never hard-coded).
 * `whole` adds a digits-only rule for keys a service reads as an integer (Tabby settlement hold days).
 * Returns the message to show, or null.
 */
export function numberError(
  entry: Pick<DynamicConfigEntry, 'min' | 'max'>,
  raw: string,
  opts: { whole?: boolean } = {},
): string | null {
  const range = entry.min !== null && entry.max !== null
    ? `${opts.whole ? 'Must be a whole number, ' : 'Must be '}${entry.min}–${entry.max}`
    : null;
  const t = raw.trim();
  const n = Number(t);
  if (t === '' || !Number.isFinite(n)) return range ? `Enter a number. ${range}` : 'Enter a number';
  if ((entry.min !== null && n < entry.min) || (entry.max !== null && n > entry.max)) return range ?? 'Out of range';
  // A whole-number key is read with DynamicConfig.integer (Integer.parseInt): "10.0" or "1e1" would
  // fail to parse there and the service would silently fall back to its default. Digits only.
  if (opts.whole && !/^\d+$/.test(t)) return range ?? 'Must be a whole number';
  if (!opts.whole && /\.\d{3,}$/.test(t)) return 'Use at most 2 decimals';
  return null;
}
